// Global Instructions Rule Applied!
//
// Critical Technology Area (CTA) — the one field builder every surface uses.
//
// The CTA is the SBA/DoD technology category an SBIC-fund investment falls
// under. It sits immediately below Industry on the company and deal forms, and
// appears on the deal commit gate when the commitment is against an SBIC fund
// (or has not been entered yet). Three surfaces, one builder, so the label, the
// help text, the patch target and the "optional" rule cannot drift apart.
//
// Nothing here hardcodes the vocabulary: the areas, their summary lines and
// their full definitions all come from cfg.critical_technology_areas via the
// synthetic lookup group in get_global_configs_v3. Reword a definition in cfg
// and every tooltip below changes with no deploy.

import React from 'react'
import { FieldTypeEnum, SelectTypeEnum, type SelectFieldConfig } from '../../core/components/InputManager/types.ts'
import { LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA } from '../../state_manager/constants/lookupCategories.ts'
import { useLookups, type LookupOption } from './hooks/useLookups.ts'

/**
 * The field label. Abbreviated deliberately (owner instruction 2026-09-16):
 * "Critical Technology Area" forced a ~216px label column on the company and
 * deal forms, whose inline layout sizes every label to the widest one — so one
 * long label pushed every OTHER field's value out with it. "CTA" costs nothing
 * in comprehension here because the help "?" sits directly beside it and opens
 * with the full name; the abbreviation is never the only thing on screen.
 */
export const CRITICAL_TECHNOLOGY_AREA_LABEL = 'CTA'

/** Spelled out — the tooltip heading, and anywhere there is room to say it. */
export const CRITICAL_TECHNOLOGY_AREA_FULL_NAME = 'Critical Technology Area'

/** The accounts column the field writes to. */
export const CRITICAL_TECHNOLOGY_AREA_COLUMN = 'critical_technology_area'

const INTRO =
  'The technology categories the DoD National Defense Science and Technology Strategy names as vital to ' +
  'national security, as published by the SBA Office of Strategic Capital. Record one on companies an SBIC ' +
  'fund invests in. Optional — leave it blank when the company falls under none of them.'

/**
 * The help "?" content: the whole published vocabulary, each area with its full
 * definition. Scrolls, because 14 definitions do not fit a tooltip — the option
 * list carries the one-line summaries for choosing, this carries the full text
 * for understanding.
 *
 * A COMPONENT, not a value built from a passed-in array, because the help must
 * not depend on the call site having lookup options to hand. The New Deal
 * drawer builds its new-company fields without them, and an options-shaped
 * helper silently produced a field with no "?" there while the select itself
 * still worked — the field looked fine and the definitions were simply gone.
 * Reading the lookups here makes that impossible: every call site gets the
 * same panel, and none of them has to remember to supply anything.
 *
 * Sorted by name to match the option list, which SelectInput sorts the same way.
 * cfg's own sort_order is the DoD's publication order; reading the panel in one
 * order and then hunting the list in another is a needless second search.
 *
 * Renders null when the lookup is empty (a cfg the backend has not cached yet),
 * so the "?" shows nothing rather than an empty panel.
 */
export function CriticalTechnologyAreaHelp() {
  const { getLookupOptions } = useLookups()
  const options = getLookupOptions(LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA)
  return buildCriticalTechnologyAreaHelp(options)
}

/** The panel's markup, for a caller that already holds the options. */
export function buildCriticalTechnologyAreaHelp(options: LookupOption[]): React.ReactElement | null {
  if (!options || options.length === 0) return null
  const ordered = [...options].sort((a, b) => a.label.localeCompare(b.label))
  return (
    <div className='text-xs'>
      <div className='font-medium mb-1'>{CRITICAL_TECHNOLOGY_AREA_FULL_NAME}s (CTA)</div>
      <div className='mb-2 opacity-90'>{INTRO}</div>
      <div className='max-h-72 overflow-y-auto pr-1'>
        {ordered.map((option) => (
          <div key={String(option.value)} className='mb-2'>
            <div className='font-medium'>{option.label}</div>
            <div className='opacity-90'>{option.description || option.shortDescription || ''}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export interface CriticalTechnologyAreaFieldOptions {
  /** getLookupOptions(LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA). Optional: the
   *  select resolves the category itself, and the help panel reads the lookups
   *  on its own — pass it only where the caller already has the list. */
  options?: LookupOption[]
  /** Current value (an accounts.critical_technology_area uuid). */
  value: unknown
  /** The record key the form data is held under — `accounts__critical_technology_area`
   *  on the company and deal forms; whatever local key the commit modal uses. */
  key: string
  editable?: boolean
  /** Set only where the field autosaves straight to PATCH /accounts. */
  patchEndpoint?: string
  /** Extra patch fields (the deal form has to name the account id). */
  valueTransformer?: SelectFieldConfig['valueTransformer']
}

/**
 * The CTA select. Deliberately never `required`: an SBIC fund may hold
 * investments that fall under no CTA, so the commit gate PROMPTS for it and
 * lets you proceed without one (owner instruction 2026-09-16).
 */
export function buildCriticalTechnologyAreaField(
  config: CriticalTechnologyAreaFieldOptions
): SelectFieldConfig {
  const field: SelectFieldConfig = {
    key: config.key,
    label: CRITICAL_TECHNOLOGY_AREA_LABEL,
    type: FieldTypeEnum.SELECT,
    value: (config.value as string) || '',
    editable: config.editable !== false,
    selectType: SelectTypeEnum.LOOKUP,
    lookupCategory: LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA,
    options: config.options,
    allowClear: true,
    showSearch: true,
    // The component, not a prebuilt node: it reads the lookups itself, so the
    // "?" is present at every call site regardless of what the caller passed.
    labelTooltip: <CriticalTechnologyAreaHelp />
  }
  if (config.patchEndpoint) field.patchEndpoint = config.patchEndpoint
  if (config.valueTransformer) field.valueTransformer = config.valueTransformer
  return field
}
